import React from 'react';
import { Box, Typography, Paper, Alert, Stack } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { hasMagazine, isEmptyPot, formatCurToolSince } from './eqpMagazineFormat';
import { getToolLevelColor, clampRatioPct } from './eqpToolLifeFormat';

/**
 * 매거진 탭 좌측 - 주축 공구 카드 + (매핑이 있으면) 포트 그리드, 없으면 안내.
 * 포트 색은 그 포트에 꽂힌 공구의 usage.tools 레벨(여유/임박/초과)을 따른다.
 */
const EqpMagazinePotPanel = ({ magazine, curTool, curToolSince, curToolUsedMin, toolByNo }) => {
  const theme = useTheme();

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
        }}
      >
        <Typography variant="h4" color="primary" sx={{ fontWeight: 700, minWidth: 56 }}>
          {curTool != null ? `T${curTool}` : '-'}
        </Typography>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>주축 공구</Typography>
          <Typography variant="caption" color="text.secondary">
            {curToolSince ? `${formatCurToolSince(curToolSince)} 장착` : '장착 시각 미확인'}
            {curToolUsedMin != null && ` · 이후 가공 ${Math.round(curToolUsedMin).toLocaleString()}분`}
          </Typography>
        </Box>
      </Paper>

      {hasMagazine(magazine) ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 1 }}>
          {magazine.map((pot) => {
            const empty = isEmptyPot(pot);
            const t = !empty ? toolByNo?.[pot.tool] : null;
            const isSpindle = !empty && pot.tool === curTool;
            return (
              <Paper
                key={pot.pot}
                variant="outlined"
                sx={{
                  p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5,
                  bgcolor: empty ? 'action.hover' : 'background.paper',
                  borderStyle: empty ? 'dashed' : 'solid',
                  borderColor: isSpindle ? 'primary.main' : 'divider',
                  borderWidth: isSpindle ? 2 : 1,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  P{String(pot.pot).padStart(2, '0')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: isSpindle ? 'primary.main' : (empty ? 'text.disabled' : 'text.primary') }}
                >
                  {empty ? '비어 있음' : (isSpindle ? '주축' : `T${pot.tool}`)}
                </Typography>
                {!empty && (
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: theme.palette.grey[200], overflow: 'hidden' }}>
                    <Box
                      sx={{
                        height: '100%',
                        width: `${clampRatioPct(t?.ratio)}%`,
                        bgcolor: getToolLevelColor(theme, t?.level),
                      }}
                    />
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Alert severity="info">
          포트 위치는 아직 몰라요 — <strong>공구 진단</strong> 탭의 매거진 위치 찾기(스캔)로 찾으면 여기에 표시돼요.
        </Alert>
      )}

      {hasMagazine(magazine) && (
        <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1.5 }} useFlexGap>
          <LegendDot color={theme.palette.success.main} label="여유" />
          <LegendDot color={theme.palette.warning.main} label="교체 임박" />
          <LegendDot color={theme.palette.error.main} label="기준 초과" />
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, border: 2, borderColor: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary">주축 포트</Typography>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

const LegendDot = ({ color, label }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Stack>
);

export default EqpMagazinePotPanel;
